const CIDR_REGEX =
	/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(3[0-2]|[12]?[0-9])$/

export const CIDR_FORMAT_MESSAGE = 'Must be in format A.B.C.D/M (e.g. 10.0.1.0/24)'

export const COMMA_SEPARATED_CIDR_LIST_MESSAGE =
	'Each value must be A.B.C.D/M. For multiple blocks, separate with commas only (e.g. 10.0.1.0/24, 10.0.2.0/24). Do not end with a comma.'

export const COMMA_SEPARATED_CIDR_TRAILING_COMMA_MESSAGE =
	'Remove the trailing comma from the CIDR list.'

export const COMMA_SEPARATED_CIDR_DUPLICATE_MESSAGE =
	'Duplicate CIDR values are not allowed in the list.'

export const PRIVATE_TGW_SUBNETS_CIDR_PLACEHOLDER = 'e.g. 10.0.1.0/24, 10.0.2.0/24'

export const PRIVATE_TGW_SUBNETS_CIDR_DESCRIPTION =
	'One or more CIDR blocks. Separate multiple values with commas only.'

export const SINGLE_CIDR_MESSAGE =
	'Only one CIDR block is allowed (e.g. 10.0.0.0/16).'

export const VPC_CIDR_BLOCK_PLACEHOLDER = 'e.g. 10.0.0.0/16'

export function getCidrError(value: string): string | null {
	if (!value || value.trim() === '') return null
	return CIDR_REGEX.test(value.trim()) ? null : CIDR_FORMAT_MESSAGE
}

/** Validates a single CIDR only — rejects comma- or whitespace-separated lists. */
export function getSingleCidrError(value: string): string | null {
	if (!value || value.trim() === '') return null

	const trimmed = value.trim()
	if (trimmed.includes(',')) {
		return SINGLE_CIDR_MESSAGE
	}

	if (/\s/.test(trimmed)) {
		const tokens = trimmed.split(/\s+/).filter(Boolean)
		if (tokens.length > 1) {
			return SINGLE_CIDR_MESSAGE
		}
	}

	return getCidrError(trimmed)
}

export function isValidCidr(value: string): boolean {
	return getCidrError(value) === null
}

/** Split comma-separated CIDR list; trims optional spaces around each comma. */
export function splitCommaSeparatedCidrList(value: string): string[] {
	return value
		.split(',')
		.map((s) => s.trim())
		.filter((s) => s.length > 0)
}

function hasDuplicateCidrValues(parts: string[]): boolean {
	const seen = new Set<string>()
	for (const part of parts) {
		const key = part.trim().toLowerCase()
		if (seen.has(key)) return true
		seen.add(key)
	}
	return false
}

/**
 * Validates one or more CIDRs separated by commas only (not whitespace).
 * Spaces after commas are allowed: `10.0.1.0/24, 10.0.2.0/24`
 */
export function getCommaSeparatedCidrListError(value: string): string | null {
	if (!value || value.trim() === '') return null

	const trimmed = value.trim()
	if (/,(\s*)$/.test(trimmed)) {
		return COMMA_SEPARATED_CIDR_TRAILING_COMMA_MESSAGE
	}
	if (!trimmed.includes(',') && /\s/.test(trimmed)) {
		const tokens = trimmed.split(/\s+/).filter(Boolean)
		if (tokens.length > 1) {
			return COMMA_SEPARATED_CIDR_LIST_MESSAGE
		}
	}

	const parts = splitCommaSeparatedCidrList(trimmed)
	if (parts.length === 0) return null

	for (const part of parts) {
		const err = getCidrError(part)
		if (err) {
			return parts.length > 1 ? COMMA_SEPARATED_CIDR_LIST_MESSAGE : err
		}
	}

	if (parts.length > 1 && hasDuplicateCidrValues(parts)) {
		return COMMA_SEPARATED_CIDR_DUPLICATE_MESSAGE
	}

	return null
}

/** @alias getCommaSeparatedCidrListError */
export const getPrivateTgwSubnetsCidrListError = getCommaSeparatedCidrListError
